// PayHere Payment Gateway Service
// Documentation: https://www.payhere.lk/downloads/checkout/

export interface PayHerePayment {
  sandbox: boolean; // true for testing, false for production
  merchant_id: string; // Merchant ID from PayHere
  return_url?: string; // Optional return URL
  cancel_url?: string; // Optional cancel URL
  notify_url: string; // Backend webhook URL
  order_id: string; // Unique order ID
  items: string; // Item description
  amount: string; // Payment amount
  currency: string; // Currency (LKR, USD, etc.)
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  country?: string;
  hash?: string; // MD5 hash for security
  custom_1?: string; // Custom field 1 (companyId)
  custom_2?: string; // Custom field 2 (subscriptionId)
  custom_3?: string; // Custom field 3 (months)
}

export interface PayHereConfig {
  merchantId: string;
  merchantSecret: string;
  notifyUrl: string;
  returnUrl?: string;
  cancelUrl?: string;
  sandbox: boolean;
}

declare global {
  interface Window {
    payhere: {
      startPayment: (payment: PayHerePayment) => void;
      onCompleted: (orderId: string) => void;
      onDismissed: () => void;
      onError: (error: string) => void;
    };
  }
}

class PayHereService {
  private config: PayHereConfig;

  constructor() {
    // Default configuration - should be loaded from environment variables
    this.config = {
      merchantId: process.env.REACT_APP_PAYHERE_MERCHANT_ID || "1221149", // Test merchant ID
      merchantSecret:
        process.env.REACT_APP_PAYHERE_MERCHANT_SECRET || "test_secret",
      notifyUrl:
        process.env.REACT_APP_API_URL + "/company/payment/payhere/notify" ||
        "http://localhost:5000/company/payment/payhere/notify",
      returnUrl: window.location.origin + "/payment-success",
      cancelUrl: window.location.origin + "/payment-cancel",
      sandbox: process.env.REACT_APP_PAYHERE_SANDBOX === "true" || true, // Default to sandbox
    };
  }

  // Check if PayHere SDK is loaded
  isPayHereLoaded(): boolean {
    return typeof window.payhere !== "undefined";
  }

  // Initialize payment
  async initiatePayment(
    orderId: string,
    amount: number,
    currency: string,
    itemDescription: string,
    hash: string, // Hash from backend
    merchantId?: string, // Optional merchant ID override
    companyId?: number, // Company ID for webhook
    subscriptionId?: number, // Subscription ID for webhook
    months?: number, // Number of months for subscription extension
    customerDetails?: {
      firstName?: string;
      lastName?: string;
      email?: string;
      phone?: string;
      address?: string;
      city?: string;
      country?: string;
    },
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.isPayHereLoaded()) {
        reject(new Error("PayHere SDK not loaded"));
        return;
      }

      const payment: PayHerePayment = {
        sandbox: this.config.sandbox,
        merchant_id: merchantId || this.config.merchantId,
        return_url: this.config.returnUrl,
        cancel_url: this.config.cancelUrl,
        notify_url: this.config.notifyUrl,
        order_id: orderId,
        items: itemDescription,
        amount: amount.toFixed(2),
        currency: currency,
        hash: hash, // Use hash from backend
        first_name: customerDetails?.firstName || "",
        last_name: customerDetails?.lastName || "",
        email: customerDetails?.email || "",
        phone: customerDetails?.phone || "",
        address: customerDetails?.address || "",
        city: customerDetails?.city || "",
        country: customerDetails?.country || "Sri Lanka",
        custom_1: companyId?.toString(), // Pass company ID for webhook
        custom_2: subscriptionId?.toString(), // Pass subscription ID for webhook
        custom_3: months?.toString(), // Pass months for subscription extension
      };

      // Set up event handlers
      window.payhere.onCompleted = (orderId: string) => {
        console.log("Payment completed. Order ID:", orderId);
        resolve();
      };

      window.payhere.onDismissed = () => {
        console.log("Payment dismissed");
        reject(new Error("Payment dismissed by user"));
      };

      window.payhere.onError = (error: string) => {
        console.error("Payment error:", error);
        reject(new Error(`Payment error: ${error}`));
      };

      // Start payment
      window.payhere.startPayment(payment);
    });
  }

  // Generate hash for payment (optional, can be done on backend)
  generateHash(
    merchantId: string,
    orderId: string,
    amount: string,
    currency: string,
    merchantSecret: string,
  ): string {
    // This should ideally be done on the backend for security
    // Hash format: MD5(merchant_id + order_id + amount + currency + merchant_secret)
    const str = merchantId + orderId + amount + currency + merchantSecret;
    // Note: You'll need to implement MD5 hashing or do this on backend
    return str;
  }

  getConfig(): PayHereConfig {
    return this.config;
  }

  updateConfig(config: Partial<PayHereConfig>): void {
    this.config = { ...this.config, ...config };
  }
}

const payhereServiceInstance = new PayHereService();
export default payhereServiceInstance;
